import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { UsePaginationReturn } from "@/hooks/use-pagination";
import type { PaginationMeta } from "@/types/common";
import { cn } from "@/lib/utils";

interface Props {
  meta: PaginationMeta;
  pagination: UsePaginationReturn;
}

const ELLIPSIS = "…" as const;

/**
 * 숫자 페이지 버튼 목록 — 처음/끝 + 현재 ±2, 사이는 ellipsis.
 * 예) 현재 6, 전체 12 → 1 … 4 5 6 7 8 … 12
 */
function buildPageItems(
  current: number,
  total: number,
): Array<number | typeof ELLIPSIS> {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const pages = new Set<number>([1, total]);
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 1 && p <= total) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const items: Array<number | typeof ELLIPSIS> = [];
  for (let i = 0; i < sorted.length; i += 1) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) items.push(ELLIPSIS);
    items.push(sorted[i]);
  }
  return items;
}

export function ContestSubmissionPagination({ meta, pagination }: Props) {
  const rangeStart = meta.totalCount === 0 ? 0 : (meta.currentPage - 1) * meta.limit + 1;
  const rangeEnd = Math.min(meta.currentPage * meta.limit, meta.totalCount);
  const totalPages = Math.max(meta.totalPages, 1);
  const pageItems = buildPageItems(meta.currentPage, totalPages);

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
      <p className="text-sm text-muted-foreground text-center md:text-left">
        <span className="hidden md:inline">
          {meta.totalCount.toLocaleString()}개 중 {rangeStart.toLocaleString()} -{" "}
          {rangeEnd.toLocaleString()}
        </span>
        <span className="md:hidden">
          {rangeStart}-{rangeEnd} / {meta.totalCount.toLocaleString()}
        </span>
      </p>
      <div className="flex items-center justify-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={pagination.prevPage}
          disabled={!meta.hasPrevPage}
          aria-label="이전 페이지"
          className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {pageItems.map((item, index) =>
          item === ELLIPSIS ? (
            <span
              key={`ellipsis-${index}`}
              className="px-1 text-sm text-muted-foreground"
            >
              {ELLIPSIS}
            </span>
          ) : (
            <Button
              key={item}
              variant={item === meta.currentPage ? "default" : "outline"}
              size="sm"
              onClick={() => pagination.setPage(item)}
              aria-label={`${item}페이지로 이동`}
              aria-current={item === meta.currentPage ? "page" : undefined}
              className={cn(
                "min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-[34px] px-2 tabular-nums",
              )}
            >
              {item}
            </Button>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={pagination.nextPage}
          disabled={!meta.hasNextPage}
          aria-label="다음 페이지"
          className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
